import React, { useState, useMemo } from 'react';
import {
  Users,
  Layers,
  ChevronRight,
  ChevronDown,
  X,
  Info,
  Receipt,
  Scale,
  Wheat,
  Activity,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { TabularNumber } from '@/components/common/TabularNumber';
import {
  CyclePerformanceData,
  RaiserPerformanceData,
} from '@/services/reportService';
import { formatCurrency, formatNumber, formatDate } from '@/lib/utils';

interface ProductionPerformanceViewProps {
  cycles: CyclePerformanceData[];
  raisers: RaiserPerformanceData[];
  selectedYear?: string;
}

export const ProductionPerformanceView: React.FC<ProductionPerformanceViewProps> = ({
  cycles,
  raisers,
  selectedYear = 'all',
}) => {
  const [viewMode, setViewMode] = useState<'cycles' | 'raisers'>('cycles');
  const [expandedCycleId, setExpandedCycleId] = useState<string | null>(null);
  const [selectedRaiserDrawer, setSelectedRaiserDrawer] = useState<RaiserPerformanceData | null>(null);

  // Sorting
  const [sortField, setSortField] = useState<string>('cycleCode');
  const [sortAsc, setSortAsc] = useState<boolean>(false);

  const sortedCycles = useMemo(() => {
    return [...cycles].sort((a: any, b: any) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (typeof aVal === 'string') {
        return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortAsc ? aVal - bVal : bVal - aVal;
    });
  }, [cycles, sortField, sortAsc]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* View Mode Toggle Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-2.5 sm:p-3 border border-slate-200 rounded-xl shadow-sm">
        <div className="grid grid-cols-2 sm:flex sm:items-center gap-1 p-1 bg-slate-100 rounded-lg w-full sm:w-fit">
          <button
            onClick={() => setViewMode('cycles')}
            className={`min-h-[44px] px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all text-center ${
              viewMode === 'cycles'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Layers className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
            <span className="hidden sm:inline">View by Production Cycle ({cycles.length})</span>
            <span className="sm:hidden">Cycles ({cycles.length})</span>
          </button>
          <button
            onClick={() => setViewMode('raisers')}
            className={`min-h-[44px] px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all text-center ${
              viewMode === 'raisers'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Users className="w-3.5 h-3.5 text-indigo-700 shrink-0" />
            <span className="hidden sm:inline">View by Raiser ({raisers.length})</span>
            <span className="sm:hidden">Raisers ({raisers.length})</span>
          </button>
        </div>

        <div className="text-xs text-slate-500 font-medium">
          {viewMode === 'cycles'
            ? `Cycle-level direct debits, revenue, and gross profit for ${selectedYear && selectedYear !== 'all' ? `Year ${selectedYear}` : 'All Years'}.`
            : `Aggregated performance and margins by Raiser across ${selectedYear && selectedYear !== 'all' ? `Year ${selectedYear}` : 'All Years'}.`}
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* SUB-VIEW 1: VIEW BY PRODUCTION CYCLE                                 */}
      {/* -------------------------------------------------------------------- */}
      {viewMode === 'cycles' && (
        <>
          {/* Desktop 8-Column Table (Hidden on Mobile) */}
          <div className="hidden md:block bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200 select-none">
                <tr>
                  <th className="py-2.5 px-3 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('cycleCode')}>
                    Production Cycle
                  </th>
                  <th className="py-2.5 px-3 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('growerName')}>
                    Raiser
                  </th>
                  <th className="py-2.5 px-3 w-20">Status</th>
                  <th className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200" onClick={() => handleSort('soldHead')}>
                    Pigs Sold
                  </th>
                  <th className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200" onClick={() => handleSort('feedCost')}>
                    Feed Cost (₱)
                  </th>
                  <th className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200" onClick={() => handleSort('totalRevenue')}>
                    Sales Revenue (₱)
                  </th>
                  <th className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200" onClick={() => handleSort('grossProfit')}>
                    Gross Profit (₱)
                  </th>
                  <th className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200" onClick={() => handleSort('grossMarginPct')}>
                    Margin %
                  </th>
                  <th className="py-2.5 px-2 text-center w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {sortedCycles.map((c) => {
                  const isExpanded = expandedCycleId === c.cycleId;
                  return (
                    <React.Fragment key={c.cycleId}>
                      <tr
                        onClick={() => setExpandedCycleId(isExpanded ? null : c.cycleId)}
                        className={`hover:bg-slate-50 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50/80 font-medium' : ''}`}
                      >
                        <td className="py-2.5 px-3 font-bold font-mono text-slate-900">
                          {c.cycleCode}
                        </td>
                        <td className="py-2.5 px-3 text-slate-800">
                          <div>{c.growerName}</div>
                          <div className="text-[10px] text-slate-400">{c.farmName}</div>
                        </td>
                        <td className="py-2.5 px-3">
                          <StatusBadge status={c.status} category="cycle" />
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                          {c.soldHead > 0 ? formatNumber(c.soldHead) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                          {formatCurrency(c.feedCost)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-slate-900 font-semibold">
                          {c.totalRevenue > 0 ? formatCurrency(c.totalRevenue) : <span className="text-slate-400 font-normal">₱0.00</span>}
                        </td>
                        <td className={`py-2.5 px-3 text-right font-mono font-bold ${c.grossProfit >= 0 ? 'text-emerald-800' : 'text-rose-700'}`}>
                          {formatCurrency(c.grossProfit)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-semibold">
                          {c.grossMarginPct !== null ? (
                            <span className={c.grossMarginPct >= 0 ? 'text-emerald-700' : 'text-rose-600'}>
                              {c.grossMarginPct.toFixed(2)}%
                            </span>
                          ) : (
                            <span className="text-slate-400 font-normal">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-2 text-center text-slate-400">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                      </tr>

                      {/* Progressive Disclosure Detail Pane */}
                      {isExpanded && (
                        <tr className="bg-slate-50/90 border-b border-slate-200">
                          <td colSpan={9} className="p-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white p-3.5 rounded-lg border border-slate-200 text-xs shadow-inner">
                              <div>
                                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Pigs Placed</span>
                                <span className="font-mono font-bold text-slate-800">
                                  {c.placedHead !== null ? `${formatNumber(c.placedHead)} head` : '—'}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Mortality</span>
                                <span className="font-mono font-bold text-rose-700">
                                  {c.mortalityHead !== null ? `${c.mortalityHead} head (${c.mortalityRatePct || 0}%)` : '—'}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Sale Weight (kg)</span>
                                <span className="font-mono font-bold text-slate-800">
                                  {c.totalSoldWeightKg !== null ? `${formatNumber(c.totalSoldWeightKg)} kg` : '—'}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Days on Feed</span>
                                <span className="font-mono font-bold text-slate-800">
                                  {c.daysOnFeed !== null ? `${c.daysOnFeed} days` : '—'}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Piglet Cost (3010)</span>
                                <span className="font-mono font-semibold text-slate-700">
                                  {c.pigletCost > 0 ? formatCurrency(c.pigletCost) : '—'}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Feed Bags (3020/3021)</span>
                                <span className="font-mono font-semibold text-slate-700">
                                  {formatNumber(c.feedBags)} bags ({formatCurrency(c.feedCost)})
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Meds & Vaccines (3030/3034)</span>
                                <span className="font-mono font-semibold text-slate-700">
                                  {c.medCost > 0 ? formatCurrency(c.medCost) : '—'}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Trucking & Permits (3040/3050/3051)</span>
                                <span className="font-mono font-semibold text-slate-700">
                                  {c.otherCosts > 0 ? formatCurrency(c.otherCosts) : '—'}
                                </span>
                              </div>
                              <div className="col-span-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                                <span className="font-semibold text-slate-600">Total Cycle Debits:</span>
                                <span className="font-mono font-bold text-rose-800">{formatCurrency(c.totalProductionCost)}</span>
                              </div>
                              <div className="col-span-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                                <span className="font-semibold text-slate-600">Grower Net Payout:</span>
                                <span className="font-mono font-bold text-indigo-900">
                                  {c.growerNetPayout !== null ? formatCurrency(c.growerNetPayout) : '— (Pending)'}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Responsive Cards (Screens < 768px) */}
          <div className="md:hidden space-y-3">
            {sortedCycles.map((c) => {
              const isExpanded = expandedCycleId === c.cycleId;
              return (
                <div key={c.cycleId} className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm space-y-2.5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-bold font-mono text-slate-900 text-sm">{c.cycleCode}</div>
                      <div className="text-xs text-slate-600">{c.growerName}</div>
                      <div className="text-[10px] text-slate-400">{c.farmName}</div>
                    </div>
                    <StatusBadge status={c.status} category="cycle" />
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 text-xs">
                    <div>
                      <span className="text-[10px] text-slate-400 block uppercase">Sales Revenue</span>
                      <span className="font-mono font-bold text-slate-900">{formatCurrency(c.totalRevenue)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block uppercase">Gross Profit</span>
                      <span className={`font-mono font-bold ${c.grossProfit >= 0 ? 'text-emerald-800' : 'text-rose-700'}`}>
                        {formatCurrency(c.grossProfit)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block uppercase">Pigs Sold</span>
                      <span className="font-mono text-slate-700">
                        {c.soldHead > 0 ? `${c.soldHead} head` : '—'}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block uppercase">Margin %</span>
                      <span className="font-mono font-semibold">
                        {c.grossMarginPct !== null ? `${c.grossMarginPct.toFixed(2)}%` : '—'}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => setExpandedCycleId(isExpanded ? null : c.cycleId)}
                    className="w-full text-center py-2.5 min-h-[44px] text-xs text-indigo-700 font-semibold border-t border-slate-100 flex items-center justify-center gap-1.5 active:bg-slate-50 transition-colors"
                  >
                    <span>{isExpanded ? 'Hide Details' : 'View Full Breakdown'}</span>
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>

                  {isExpanded && (
                    <div className="bg-slate-50 p-2.5 rounded-lg space-y-1.5 text-[11px] font-mono border border-slate-200">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Pigs Placed:</span>
                        <span className="font-semibold text-slate-800">{c.placedHead !== null ? `${c.placedHead} head` : '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Mortality:</span>
                        <span className="font-semibold text-rose-700">{c.mortalityHead !== null ? `${c.mortalityHead} head` : '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Feed Cost:</span>
                        <span className="font-semibold text-slate-800">{formatCurrency(c.feedCost)} ({c.feedBags} bags)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Piglet Placement:</span>
                        <span className="font-semibold text-slate-800">{c.pigletCost > 0 ? formatCurrency(c.pigletCost) : '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Meds & Vaccines:</span>
                        <span className="font-semibold text-slate-800">{c.medCost > 0 ? formatCurrency(c.medCost) : '—'}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-slate-200">
                        <span className="text-slate-600 font-bold">Total Costs:</span>
                        <span className="font-bold text-rose-800">{formatCurrency(c.totalProductionCost)}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* -------------------------------------------------------------------- */}
      {/* SUB-VIEW 2: VIEW BY RAISER                                           */}
      {/* -------------------------------------------------------------------- */}
      {viewMode === 'raisers' && (
        <>
          {/* Desktop Raiser Table */}
          <div className="hidden md:block bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">Raiser Name</th>
                  <th className="py-2.5 px-3 text-center w-20">Cycles</th>
                  <th className="py-2.5 px-3 text-right">Pigs Sold</th>
                  <th className="py-2.5 px-3 text-right">Feed Allocated (₱)</th>
                  <th className="py-2.5 px-3 text-right">Sales Revenue (₱)</th>
                  <th className="py-2.5 px-3 text-right">Production Costs (₱)</th>
                  <th className="py-2.5 px-3 text-right">Net Profit (₱)</th>
                  <th className="py-2.5 px-3 text-right">Avg Margin %</th>
                  <th className="py-2.5 px-3 text-center w-24">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {raisers.map((r) => (
                  <tr key={r.growerName} className="hover:bg-slate-50 transition-colors">
                    <td className="py-2.5 px-3 font-bold text-slate-900">
                      {r.growerName}
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono text-slate-600">
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full font-semibold">
                        {r.totalCycles}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                      {r.totalSoldHead > 0 ? formatNumber(r.totalSoldHead) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                      {formatCurrency(r.totalFeedCost)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-900 font-semibold">
                      {formatCurrency(r.totalRevenue)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                      {formatCurrency(r.totalProductionCost)}
                    </td>
                    <td className={`py-2.5 px-3 text-right font-mono font-bold ${r.netGrossProfit >= 0 ? 'text-emerald-800' : 'text-rose-700'}`}>
                      {formatCurrency(r.netGrossProfit)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-semibold">
                      {r.avgGrossMarginPct !== null ? (
                        <span className={r.avgGrossMarginPct >= 0 ? 'text-emerald-700' : 'text-rose-600'}>
                          {r.avgGrossMarginPct.toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedRaiserDrawer(r)}
                        className="h-7 px-2 text-indigo-700 border-indigo-200 hover:bg-indigo-50 text-[11px] font-semibold"
                      >
                        History
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Raiser Cards */}
          <div className="md:hidden space-y-3">
            {raisers.map((r) => (
              <div key={r.growerName} className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-slate-900 text-sm">{r.growerName}</div>
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold">
                    {r.totalCycles} {r.totalCycles === 1 ? 'cycle' : 'cycles'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 text-xs">
                  <div>
                    <span className="text-[10px] text-slate-400 block uppercase">Total Revenue</span>
                    <span className="font-mono font-bold text-slate-900">{formatCurrency(r.totalRevenue)}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block uppercase">Net Profit</span>
                    <span className={`font-mono font-bold ${r.netGrossProfit >= 0 ? 'text-emerald-800' : 'text-rose-700'}`}>
                      {formatCurrency(r.netGrossProfit)}
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedRaiserDrawer(r)}
                  className="w-full h-10 min-h-[44px] mt-1 text-indigo-700 border-indigo-200 text-xs font-semibold active:bg-indigo-50"
                >
                  View Cycle History ({r.totalCycles})
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Raiser Cycle History Slide-Over Sheet / Drawer */}
      {selectedRaiserDrawer && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end">
          <div className="bg-white w-full max-w-xl h-full p-6 shadow-2xl flex flex-col justify-between overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                <div>
                  <h3 className="font-bold text-slate-900 text-base">{selectedRaiserDrawer.growerName}</h3>
                  <p className="text-xs text-slate-500">Historical performance across {selectedRaiserDrawer.totalCycles} cycles</p>
                </div>
                <button
                  onClick={() => setSelectedRaiserDrawer(null)}
                  className="p-1 rounded-md text-slate-400 hover:text-slate-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Raiser Aggregate Banner */}
              <div className="grid grid-cols-3 gap-2 bg-slate-50 p-3 rounded-lg text-xs font-mono border border-slate-200">
                <div>
                  <span className="text-[10px] text-slate-400 block font-sans">Total Revenue:</span>
                  <span className="font-bold text-slate-900">{formatCurrency(selectedRaiserDrawer.totalRevenue)}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 block font-sans">Total Costs:</span>
                  <span className="font-bold text-rose-700">{formatCurrency(selectedRaiserDrawer.totalProductionCost)}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 block font-sans">Net Profit:</span>
                  <span className="font-bold text-emerald-800">{formatCurrency(selectedRaiserDrawer.netGrossProfit)}</span>
                </div>
              </div>

              {/* List of Raiser Cycles */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Production Cycles</h4>
                {selectedRaiserDrawer.cycleCodes.map((code) => {
                  const cyc = cycles.find((c) => c.cycleCode === code);
                  if (!cyc) return null;
                  return (
                    <div key={cyc.cycleId} className="p-3 bg-white border border-slate-200 rounded-lg text-xs space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold font-mono text-slate-900">{cyc.cycleCode}</span>
                        <StatusBadge status={cyc.status} category="cycle" />
                      </div>
                      <div className="grid grid-cols-3 gap-1 font-mono text-[11px] pt-1 text-slate-600">
                        <div>Revenue: <span className="font-semibold text-slate-800">{formatCurrency(cyc.totalRevenue)}</span></div>
                        <div>Costs: <span className="font-semibold text-slate-800">{formatCurrency(cyc.totalProductionCost)}</span></div>
                        <div>Profit: <span className="font-semibold text-emerald-800">{formatCurrency(cyc.grossProfit)}</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <Button
                variant="outline"
                onClick={() => setSelectedRaiserDrawer(null)}
                className="w-full h-11 min-h-[44px] text-xs font-semibold"
              >
                Close History
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
